import { downloadFilename, languageByCode, languageFilename, toWebVtt, type CaptionCue } from "@veyso/core";
import type { LanguageView, LanguagesView } from "../api.js";

/**
 * The Export page's languages, in words and arithmetic: one row per video it
 * can make, what each row's state is, and what each file is called. Kept
 * apart from the components so the tests at the root can run them without React.
 */

export interface ExportRow {
  /** Null for the English, the video the project was made in. */
  code: string | null;
  label: string;
  depthLabel: string;
  /** English is always "not-exported" here; the page knows its own exports and says so. */
  state: "exported" | "not-exported";
  /**
   * The English changed since this language was made. It is still ready and
   * paid for, so it is exported as it is; the row offers "Update" beside it.
   */
  outOfDate: boolean;
  /** The language's other files, beside its video. */
  extras: { label: string; href: string }[];
}

const EXTRAS: { kind: keyof LanguageView["downloads"]; label: string }[] = [
  { kind: "vtt", label: ".vtt" },
  { kind: "srt", label: ".srt" },
  { kind: "script", label: "Script" },
  { kind: "audio", label: "Voice" },
];

/** English first, then every ready language in the view's order: only a ready language can be exported. */
export function exportRows(view: Pick<LanguagesView, "languages"> | null): ExportRow[] {
  const english: ExportRow = {
    code: null,
    label: "English",
    depthLabel: "Original",
    state: "not-exported",
    outOfDate: false,
    extras: [],
  };
  const languages = (view?.languages ?? [])
    .filter((language) => language.status === "ready")
    .map((language): ExportRow => ({
      code: language.code,
      label: language.nativeName,
      depthLabel: language.depth === "video" ? "Whole video" : "Voice and subtitles",
      state: language.downloads.video ? "exported" : "not-exported",
      outOfDate: language.outOfDate,
      extras: EXTRAS.flatMap(({ kind, label }) => {
        const href = language.downloads[kind];
        return href ? [{ label, href }] : [];
      }),
    }));
  return [english, ...languages];
}

/**
 * What a row's MP4 is called: the project's name, and for a language its
 * English name after it — the server's own scheme, the same as its .vtt and .srt.
 */
export function exportFilename(projectName: string, code: string | null): string {
  const language = code ? languageByCode(code) : undefined;
  return language ? languageFilename(projectName, language, "mp4") : downloadFilename(projectName, "mp4");
}

/** One video the Export page's player can play: the English, or a language's own MP4. */
export interface PlayerChoice {
  /** Null for the English. */
  code: string | null;
  label: string;
  /** What the player loads: the file itself, not a download of it. */
  src: string;
  downloadUrl: string;
  filename: string;
  /** Known for the English only; a language's export is not measured here. */
  sizeBytes: number | null;
  /** The language's subtitles, for the player's CC; none for the English. */
  cues: CaptionCue[];
}

/**
 * What the player can switch between: the English when it has been exported,
 * then every language with an exported MP4, in the view's order. A language
 * not yet exported is not offered — there is nothing to play.
 */
export function playerChoices(
  english: { url: string; downloadUrl: string; filename: string | null; sizeBytes: number | null } | null,
  view: Pick<LanguagesView, "languages"> | null,
  projectName: string,
): PlayerChoice[] {
  const first: PlayerChoice[] = english
    ? [
        {
          code: null,
          label: "English",
          src: english.url,
          downloadUrl: english.downloadUrl,
          filename: english.filename ?? exportFilename(projectName, null),
          sizeBytes: english.sizeBytes,
          cues: [],
        },
      ]
    : [];
  const languages = (view?.languages ?? []).flatMap((language): PlayerChoice[] => {
    const video = language.downloads.video;
    if (!video) return [];
    return [
      {
        code: language.code,
        label: language.nativeName,
        src: video.replace(/\?download$/, ""),
        downloadUrl: video,
        filename: exportFilename(projectName, language.code),
        sizeBytes: null,
        cues: language.cues,
      },
    ];
  });
  return [...first, ...languages];
}

/**
 * A language's subtitles as a WebVTT file the player can load. Built here from
 * the cues rather than pointing at the stored .vtt: on object storage that is
 * a redirect to another origin, and a <track> from another origin needs CORS.
 */
export function subtitleTrack(cues: readonly CaptionCue[]): string | null {
  return cues.length > 0 ? toWebVtt(cues.map(({ start, end, text }) => ({ start, end, text }))) : null;
}

/** The key a row is checked under: its code, or "" for the English. */
export const rowKey = (row: Pick<ExportRow, "code">): string => row.code ?? "";

/**
 * Which rows start checked: the English, or — when the page was opened for a
 * language (`?lang=de`) — only that one, out of date or not. A code the rows
 * do not offer is ignored.
 */
export function initialChecked(rows: readonly ExportRow[], lang: string | null): string[] {
  const asked = lang ? rows.find((row) => row.code === lang) : undefined;
  return asked ? [rowKey(asked)] : [""];
}

/**
 * What the page says at the top when it was opened for a language
 * (`?lang=de`) this browser can't render — rather than leaving the button
 * at "Pick a video" with no reason. Null otherwise, and while it is still
 * being asked whether this browser can render.
 */
export function cannotRenderNotice(
  rows: readonly ExportRow[],
  lang: string | null,
  canRender: boolean | null,
): string | null {
  if (!lang || canRender !== false) return null;
  const asked = rows.find((row) => row.code === lang);
  return asked ? `This browser can't render ${asked.label} — use Chrome on a computer.` : null;
}

/** The export button at rest: one video, several, or none picked. */
export function exportCountLabel(count: number): string {
  if (count === 0) return "Pick a video";
  return count === 1 ? "Export video" : `Export ${count} videos`;
}

/** Where one row is in an export that is running. */
export type RowRun =
  | { phase: "waiting" }
  | { phase: "rendering" | "saving"; progress: number; where?: "servers" }
  | { phase: "failed"; message: string };

export const CANNOT_RENDER_HERE = "This browser can't render here — use Chrome on a computer.";
export const LANGUAGE_CHANGED = "This language changed while it was rendering — export it again.";

/**
 * What "Export N videos" makes, in order: the English first when it is
 * checked, then each checked language in the rows' order — an out-of-date one
 * included, as it is. No language at all when this browser can't render here
 * (languages are rendered only in the browser).
 */
export function exportQueue(
  checked: readonly string[],
  rows: readonly ExportRow[],
  canRender: boolean | null,
): string[] {
  return rows
    .filter((row) => checked.includes(rowKey(row)))
    .filter((row) => row.code === null || canRender !== false)
    .map(rowKey);
}

/** The languages still to render, the first being rendered now, and what each row says. */
export interface ExportState {
  queue: string[];
  runs: Record<string, RowRun>;
}

/** How one language's render ended. */
export type ExportOutcome = "done" | "changed" | "cannot" | "failed" | "stopped";

/** The languages of an export, all waiting. */
export function startExport(codes: readonly string[]): ExportState {
  return { queue: [...codes], runs: Object.fromEntries(codes.map((code) => [code, { phase: "waiting" } as RowRun])) };
}

/** Stopped between rows: the rows still waiting are dropped, the rest keep what they say. */
export function stopExport(state: ExportState): ExportState {
  const runs = { ...state.runs };
  for (const code of state.queue) delete runs[code];
  return { queue: [], runs };
}

const MESSAGES: Record<Exclude<ExportOutcome, "done">, string> = {
  changed: LANGUAGE_CHANGED,
  cannot: CANNOT_RENDER_HERE,
  failed: "That render did not finish in your browser. Try again.",
  stopped: "Stopped before it finished.",
};

/**
 * The export after the language at the head of the queue ends. A finished
 * one says nothing (the view says "Exported"); any other says why on its row,
 * and the next row goes on — unless it was stopped, which drops the rest.
 */
export function nextExport(state: ExportState, outcome: ExportOutcome): ExportState {
  const [head, ...rest] = state.queue;
  if (head === undefined) return state;
  const runs = { ...state.runs };
  if (outcome === "done") delete runs[head];
  else runs[head] = { phase: "failed", message: MESSAGES[outcome] };
  const next = { queue: rest, runs };
  return outcome === "stopped" ? stopExport(next) : next;
}
